import * as ecapa from "../../lib/ecapa.js";
import * as telemetry from "../../lib/telemetry.js";

import { useApp, type ModelReadiness } from "../state/store";

/**
 * The one way anything gets the voice model ready (#658, #659).
 *
 * Boot used to load the 84 MB model before the app rendered at all, so signing in, reading
 * past meetings or opening notes waited on a download none of them needs; [V] on 2026-10-05
 * a bad saved copy and a stale Worker left two phones on "Getting ready" with no way in.
 * Now boot never touches it:
 *
 * - **Home warms it** (`warmModel`), in the background, unless the connection is metered
 *   and there is no saved copy, in which case it waits to be asked.
 * - **Voice setup, re-record, Start and Join ask for it** (`ensureModelReady`). Ready is
 *   instant. A load already running is joined, never started twice, and its progress is
 *   in the store for every screen that waits on it. A failed load is retried once; a
 *   second failure is the plain error with Try again.
 *
 * A waiter that gives up (Cancel, leaving the screen) stops waiting; the load itself keeps
 * going, because the next tap will want it.
 *
 * `lib/ecapa.js` keeps every #643 guarantee underneath: no partial copy stored, Range
 * resume, a bad copy evicted, bounded waits.
 */

/** Why a load failed, in the two sentences the screen can say. */
export type ModelFailure = NonNullable<ModelReadiness["failure"]>;

export class ModelLoadError extends Error {
  readonly failure: ModelFailure;
  constructor(failure: ModelFailure, cause?: unknown) {
    super(failure === "offline" ? "voice model needs a download and the device is offline" : "voice model did not load");
    this.name = "ModelLoadError";
    this.failure = failure;
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

/** What `navigator.connection` says, where a browser has it (Chromium only; Safari has none). */
export interface ConnectionHint {
  saveData?: boolean;
  effectiveType?: string;
  type?: string;
}

const SLOW_TYPES = new Set(["slow-2g", "2g", "3g"]);

/**
 * Whether an 84 MB download should wait to be asked for (#659).
 *
 * Data saver on, a slow connection, or a cellular one. No hint at all (Safari, Firefox)
 * reads as not metered: that is most devices, and holding them back would make every Start
 * wait on a download Home could have done.
 */
export function connectionIsMetered(connection: ConnectionHint | null | undefined): boolean {
  if (!connection) return false;
  if (connection.saveData) return true;
  if (connection.effectiveType && SLOW_TYPES.has(connection.effectiveType)) return true;
  return connection.type === "cellular";
}

function currentConnection(): ConnectionHint | undefined {
  return (globalThis.navigator as Navigator & { connection?: ConnectionHint } | undefined)?.connection;
}

function offline(): boolean {
  return globalThis.navigator?.onLine === false;
}

function setModel(patch: Partial<ModelReadiness>): void {
  useApp.setState((state) => ({ model: { ...state.model, ...patch } }));
}

/** The load in flight, shared by everyone who asks while it runs. */
let inflight: Promise<void> | null = null;
/** Off only in the parity harness; see `suspendModelWarming`. */
let warming = true;

/**
 * Stop `warmModel` from starting anything, for the development-only `/parity` harness.
 *
 * The gallery seeds the model's states into the store and renders Home and voice setup;
 * a real background load would overwrite the seeded state and pull 84 MB per capture.
 */
export function suspendModelWarming(): void {
  warming = false;
}
/** Failed loads since this page opened. A count, for `model_load_failed`. */
let failures = 0;

type Trigger = "background" | "on_demand";

/**
 * Start a load, or hand back the one already running.
 *
 * A failure here is silent in the store (it goes back to `idle`): the caller decides whether
 * it is the person's problem yet. A background failure is not -- the next tap retries -- and
 * `ensureModelReady` shows the error only once its own retry has failed too.
 */
function startLoad(trigger: Trigger): Promise<void> {
  if (ecapa.isLoaded()) {
    setModel({ status: "ready", fraction: 1, failure: null });
    return Promise.resolve();
  }
  if (inflight) return inflight;

  const t0 = performance.now();
  // Loading straight away, before the cache check: a screen waiting on this must never see
  // a stale `failed` or `idle` while it is already under way.
  setModel({ status: "loading", fraction: null, failure: null });
  const run = (async () => {
    const cached = await ecapa.isCached();
    // No saved copy and no connection: the download cannot start, and retrying it the
    // usual way would spend the backoff to say the same thing.
    if (!cached && offline()) throw new ModelLoadError("offline");
    setModel({ download: !cached, fraction: cached ? null : 0 });
    let shown = -1;
    try {
      await ecapa.load((fraction) => {
        if (fraction === null) {
          setModel({ fraction: null });
          return;
        }
        // One store write per whole percent, not one per network chunk.
        const percent = Math.floor(fraction * 100);
        if (percent === shown) return;
        shown = percent;
        setModel({ fraction });
      });
    } catch (err) {
      throw new ModelLoadError(!cached && offline() ? "offline" : "failed", err);
    }
    setModel({ status: "ready", fraction: 1, failure: null });
    const loadMs = Math.round(performance.now() - t0);
    // Read here, not at boot: `threads` is what ORT actually took only once a session exists,
    // and `proxied` is whether inference ended up off the main thread, which fails silently.
    const threading = ecapa.threading();
    telemetry.log(
      "model_load",
      {
        model_load_ms: loadMs,
        trigger,
        cached,
        threads: threading.threads,
        cores: threading.cores,
        proxied: threading.proxied,
      },
      loadMs
    );
  })();
  inflight = run;
  run.then(
    () => {
      inflight = null;
    },
    (err: unknown) => {
      inflight = null;
      failures += 1;
      const failure: ModelFailure = err instanceof ModelLoadError ? err.failure : "failed";
      telemetry.log("model_load_failed", { model_load_failures: failures, trigger, reason: failure });
      // Quietly back to idle; see the function comment for who says it out loud.
      if (useApp.getState().model.status === "loading") setModel({ status: "idle", fraction: null });
    }
  );
  return run;
}

/**
 * Warm the model in the background, from Home (and voice setup) (#659).
 *
 * Skipped on a metered connection unless a saved copy makes the load free; the first screen
 * that needs it then loads it on demand with the size note. Never throws and never shows an
 * error: a background failure is retried by the next `ensureModelReady`.
 */
export async function warmModel(): Promise<void> {
  if (!warming || ecapa.isLoaded() || inflight) return;
  if (connectionIsMetered(currentConnection()) && !(await ecapa.isCached())) return;
  startLoad("background").catch(() => {});
}

/** Rejects with an `AbortError` when `signal` fires; `promise` itself runs on. */
function untilAborted<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (err) => {
        signal.removeEventListener("abort", onAbort);
        reject(err);
      }
    );
  });
}

function abortError(): Error {
  // Never shown: a cancelled wait is not an error any screen reports (`isAbort`).
  return new DOMException("model_wait_cancelled", "AbortError");
}

/** Whether `err` is a waiter giving up, which is never an error worth showing. */
export function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/**
 * Resolve once the model is loaded (#658).
 *
 * Instant when it is. Otherwise joins the load in flight or starts one, retries once if it
 * fails, and then rejects with `ModelLoadError` and leaves `failed` in the store for the
 * screen's error and Try again. `signal` lets a screen stop waiting without stopping the load.
 */
export async function ensureModelReady(options: { signal?: AbortSignal } = {}): Promise<void> {
  const { signal } = options;
  if (ecapa.isLoaded()) {
    if (useApp.getState().model.status !== "ready") setModel({ status: "ready", fraction: 1, failure: null });
    return;
  }
  let last: unknown;
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      await untilAborted(startLoad("on_demand"), signal);
      return;
    } catch (err) {
      if (isAbort(err)) throw err;
      last = err;
      // Offline with no saved copy cannot change in the time a retry takes.
      if (err instanceof ModelLoadError && err.failure === "offline") break;
    }
  }
  const failure: ModelFailure = last instanceof ModelLoadError ? last.failure : "failed";
  setModel({ status: "failed", fraction: null, failure });
  throw last instanceof ModelLoadError ? last : new ModelLoadError(failure, last);
}

/** Whether the model is loaded now, so a screen can skip showing a wait that will not happen. */
export function isModelReady(): boolean {
  return ecapa.isLoaded();
}

/** For tests: forget the load in flight and the failure count. */
export function resetModelReadinessForTest(): void {
  inflight = null;
  failures = 0;
  warming = true;
}
